import { useEffect, useRef, useState, useCallback } from 'react';
import websocketService from '../services/websocketService';
import { showAdminNotificationToastOnce } from '../utils/adminNotificationToast';
import logger from '@/utils/logger';

// Badge + toast temps réel pour les événements admin (incident chauffeur,
// retard significatif, demande parent). Consomme l'event WebSocket
// `admin_notification` diffusé par adminNotificationService.js (backend) sur
// la room school:{schoolId}. Complète le Web Push : couvre le cas "onglet
// ouvert mais token FCM pas encore enregistré" — voir docs/ideas/admin-realtime-notifications.md.
//
// `schoolId` doit être passé par l'appelant (Header.jsx, depuis useAuth().user.school.id) —
// PAS via utils/storage.js::getSchoolId(), qui n'est écrit que par trGpsSuivi.js et
// trFleetMonitor.js (uniquement quand ces pages précises sont visitées). Utiliser ce
// storage ici ferait dépendre le badge/toast d'avoir déjà ouvert une page GPS dans la
// session — silencieusement absent sinon (bug constaté : "le toast n'arrive jamais").
export function useAdminRealtimeNotifications({ enabled = true, schoolId, navigate } = {}) {
  const [count, setCount] = useState(0);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!enabled || !schoolId) {
      return undefined;
    }

    const handleAdminNotification = (data) => {
      if (!enabledRef.current) {
        return;
      }
      setCount((c) => c + 1);
      showAdminNotificationToastOnce({
        id: data?.id,
        title: data?.title,
        message: data?.message,
        navigate: navigateRef.current,
      });
    };

    websocketService.connect();
    websocketService.joinSchoolRoom(schoolId);
    websocketService.addListener('admin_notification', handleAdminNotification);

    logger.debug('🔔 [useAdminRealtimeNotifications] Abonné à school:' + schoolId);

    return () => {
      websocketService.removeListener('admin_notification', handleAdminNotification);
      websocketService.disconnect();
    };
  }, [enabled, schoolId]);

  const resetCount = useCallback(() => setCount(0), []);

  return { notificationCount: count, resetCount };
}
